/**
 * PinnedMessagesPanel Component
 *
 * A slide-out panel showing all pinned messages in a channel.
 */
import React, { useState, useEffect } from 'react';
import api from '../../services/api';

export interface PinnedMessage {
  id: string;
  content: string;
  sender_type: string;
  sender_user_id?: string;
  sender_agent_id?: string;
  created_at: string;
  pinned_at?: string;
  pinned_by_user_id?: string;
}

interface PinnedMessagesPanelProps {
  isOpen: boolean;
  onClose: () => void;
  channelId: string;
  onMessageClick?: (messageId: string) => void;
  onUnpin?: (messageId: string) => Promise<void>;
}

export const PinnedMessagesPanel: React.FC<PinnedMessagesPanelProps> = ({
  isOpen,
  onClose,
  channelId,
  onMessageClick,
  onUnpin,
}) => {
  const [pinnedMessages, setPinnedMessages] = useState<PinnedMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && channelId) {
      loadPinnedMessages();
    }
  }, [isOpen, channelId]);

  const loadPinnedMessages = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get(`/inbox/channels/${channelId}/pinned`);
      setPinnedMessages(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load pinned messages');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnpin = async (messageId: string) => {
    if (onUnpin) {
      await onUnpin(messageId);
      setPinnedMessages(prev => prev.filter(m => m.id !== messageId));
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateContent = (content: string, maxLength: number = 150) => {
    if (content.length <= maxLength) return content;
    return content.slice(0, maxLength) + '...';
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 z-40"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-96 bg-white dark:bg-gray-900 shadow-xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
              <path d="M5 5a2 2 0 012-2h6a2 2 0 012 2v2h2a2 2 0 012 2v7a2 2 0 01-2 2H5a2 2 0 01-2-2V9a2 2 0 012-2h2V5zm4 0v2h2V5H9z" />
            </svg>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Pinned Messages
            </h2>
            <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 rounded-full text-xs text-gray-600 dark:text-gray-400">
              {pinnedMessages.length}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : error ? (
            <div className="p-4 bg-red-500/10 dark:bg-red-900/20 rounded-lg text-red-600 dark:text-red-400 text-sm">
              {error}
              <button
                onClick={loadPinnedMessages}
                className="ml-2 underline hover:no-underline"
              >
                Retry
              </button>
            </div>
          ) : pinnedMessages.length === 0 ? (
            <div className="text-center py-8">
              <svg className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 5a2 2 0 012-2h6a2 2 0 012 2v2h2a2 2 0 012 2v7a2 2 0 01-2 2H5a2 2 0 01-2-2V9a2 2 0 012-2h2V5z" />
              </svg>
              <p className="text-gray-500 dark:text-gray-400">
                No pinned messages yet
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                Pin important messages to find them quickly
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {pinnedMessages.map((message) => (
                <div
                  key={message.id}
                  className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer group"
                  onClick={() => onMessageClick?.(message.id)}
                >
                  {/* Message header */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs
                        ${message.sender_type === 'user' ? 'bg-blue-500' :
                          message.sender_type === 'agent' ? 'bg-purple-500' : 'bg-gray-500'}`}>
                        {message.sender_type === 'agent' ? 'A' : 'U'}
                      </div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                        {message.sender_type}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(message.created_at)}
                      </span>
                      {onUnpin && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUnpin(message.id);
                          }}
                          className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-opacity"
                          title="Unpin message"
                        >
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Message content */}
                  <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap break-words">
                    {truncateContent(message.content)}
                  </p>

                  {/* Pinned info */}
                  {message.pinned_at && (
                    <div className="mt-2 flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M5 5a2 2 0 012-2h6a2 2 0 012 2v2h2a2 2 0 012 2v7a2 2 0 01-2 2H5a2 2 0 01-2-2V9a2 2 0 012-2h2V5z" />
                      </svg>
                      Pinned {formatDate(message.pinned_at)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PinnedMessagesPanel;
